import React, { useState } from "react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Sparkles } from "lucide-react";

const benefits = [
  "Unlimited shift calculations", "Unlimited job comparisons", "Save calculations",
  "Saved income goals", "Advanced income planning", "Career roadmap access when released",
  "Future premium career resources",
];

export default function Upgrade() {
  const { user } = useAuth();
  const [interval, setInterval] = useState("year");
  const [loading, setLoading] = useState(false);
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";

  const checkout = async () => {
    setLoading(true);
    try {
      const lookup_key = interval === "year" ? "pro_yearly" : "pro_monthly";
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (isPro) {
    return (
      <div className="mx-auto max-w-lg rounded-3xl border border-[#D6A84B]/40 bg-gradient-to-br from-[#0F2747] to-[#16355F] p-10 text-center text-white shadow-xl">
        <Crown className="mx-auto h-10 w-10 text-[#D6A84B]" />
        <h1 className="mt-4 font-heading text-2xl font-bold">You're a Pro member 🎉</h1>
        <p className="mt-2 text-slate-300">Enjoy unlimited calculations, comparisons, and saved plans.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#D6A84B]/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#B8862F]">
          <Sparkles className="h-3.5 w-3.5" /> RN Level Up Pro
        </span>
        <h1 className="mt-4 font-heading text-3xl font-extrabold text-[#0F2747]">Level Up Your Nursing Income</h1>
        <p className="mt-2 text-slate-600">Unlock the tools to plan your next income move.</p>
      </div>

      <div className="flex justify-center gap-2 rounded-full border border-slate-200 bg-white p-1">
        <button data-testid="interval-monthly" onClick={() => setInterval("month")}
          className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all ${interval === "month" ? "bg-[#0F2747] text-white" : "text-slate-500"}`}>
          Monthly · $9.99
        </button>
        <button data-testid="interval-yearly" onClick={() => setInterval("year")}
          className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all ${interval === "year" ? "bg-[#0F2747] text-white" : "text-slate-500"}`}>
          Yearly · $79 <span className="text-[#D6A84B]">(Save 34%)</span>
        </button>
      </div>

      <div className="rounded-3xl border border-[#D6A84B]/40 bg-gradient-to-br from-[#0F2747] via-[#16355F] to-[#0F2747] p-8 text-white shadow-xl">
        <div className="flex items-end gap-2">
          <span className="font-heading text-5xl font-extrabold">{interval === "year" ? "$79" : "$9.99"}</span>
          <span className="pb-2 text-slate-300">/{interval === "year" ? "year" : "month"}</span>
        </div>
        <ul className="mt-6 space-y-3">
          {benefits.map((b) => (
            <li key={b} className="flex items-center gap-3 text-sm text-slate-100">
              <Check className="h-4 w-4 shrink-0 text-[#D6A84B]" /> {b}
            </li>
          ))}
        </ul>
        <Button onClick={checkout} disabled={loading} data-testid="start-pro-btn"
          className="mt-8 w-full rounded-full bg-[#D6A84B] py-6 text-base font-semibold text-[#0F2747] hover:bg-[#c69a3f]">
          {loading ? "Redirecting to checkout..." : "Start RN Level Up Pro"}
        </Button>
        <p className="mt-3 text-center text-xs text-slate-400">Secure checkout via Stripe. Cancel anytime.</p>
      </div>
    </div>
  );
}
